const STATES = new Set([
  'not_enabled', 'preparing', 'current', 'catching_up',
  'capacity_limited', 'delayed', 'investigation_required',
]);

const PURPOSE_LABELS = Object.freeze({
  source_document: 'source document',
  general_design_photo: 'design photo',
  completed_arrangement: 'arrangement photo',
  delivery_photo: 'delivery photo',
  delivery_signature: 'delivery signature',
  delivery_video: 'delivery video',
});

function nonnegative(value) {
  return Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function validCounts(value) {
  if (!value || typeof value !== 'object') return false;
  const eligible = nonnegative(value.eligible);
  const available = nonnegative(value.available);
  const pending = nonnegative(value.pending);
  return eligible !== null && available !== null && pending !== null
    && available <= eligible && pending === eligible - available;
}

function validTimestamp(value) {
  return value === null || (
    typeof value === 'string' && Number.isFinite(Date.parse(value))
  );
}

function validRefreshHealth(value) {
  return value === undefined || value === null || (
    typeof value.checking === 'boolean'
    && nonnegative(value.consecutiveFailures) !== null
    && validTimestamp(value.lastCompletedAt)
    && validTimestamp(value.lastFailedAt)
    && (value.consecutiveFailures === 0 || value.lastFailedAt !== null)
    && (value.failureCause === null || typeof value.failureCause === 'string')
  );
}

function validDownloadHealth(value) {
  return value === undefined || value === null || (
    ['unknown', 'reserved', 'handoff_interrupted', 'retry_delayed', 'transferring'].includes(value.state)
    && (value.reservedCount === null || nonnegative(value.reservedCount) !== null)
    && validTimestamp(value.reservationExpiresAt) && validTimestamp(value.nextCheckAt)
  );
}

export function mediaDownloadPresentation(status) {
  const download = status?.downloadHealth;
  if (!download || !validDownloadHealth(download)) return null;
  const descriptions = {
    unknown: 'A download status has not yet been established.',
    reserved: 'Reserved photo work is waiting to be checked again.',
    handoff_interrupted: 'The download handoff was interrupted. Its outcome is not yet established.',
    retry_delayed: 'A photo download is waiting for another attempt.',
    transferring: 'A photo transfer is in progress.',
  };
  return { state: download.state, detail: descriptions[download.state],
    reservationExpiresAt: download.reservationExpiresAt, nextCheckAt: download.nextCheckAt };
}

const REFRESH_FAILURE_DETAILS = new Map([
  ['metadata_request_timed_out', 'The check for newer photo information timed out.'],
  ['metadata_service_unavailable', 'The service needed to check photo information could not be reached.'],
  ['local_service_unavailable', 'The local service could not finish the photo check.'],
  ['cloud_service_unavailable', 'The online service could not finish the photo check.'],
  ['media_metadata_sync_failed', 'The photo check could not finish.'],
]);

function refreshDelayDetail(status) {
  return 'Checking for newer photos is delayed.'
    + (status?.inventoryKnown !== false && validCounts(status?.counts) && status.counts.available > 0
      ? ' Saved photos remain available.' : '');
}

export function mediaRefreshPresentation(status) {
  const health = status?.refreshHealth;
  if (!health || !validRefreshHealth(health)) return null;
  return {
    lastCompletedAt: health.lastCompletedAt,
    lastFailedAt: health.lastFailedAt,
    checking: health.checking,
    failureDetail: health.lastFailedAt
      ? (REFRESH_FAILURE_DETAILS.get(health.failureCause) || 'The photo check could not finish.') : null,
    warning: health.consecutiveFailures >= 2
      ? refreshDelayDetail(status) : null,
  };
}

const CATALOG_STATES = new Set([
  'not_available', 'preparing', 'current', 'catching_up',
  'partially_kept', 'delayed', 'investigation_required',
]);

function validReadinessStatus(value, states) {
  return Boolean(
    value
    && typeof value === 'object'
    && [1, 2].includes(value.schemaVersion)
    && states.has(value.state)
    && validCounts(value.counts)
    && validCounts(value.bytes)
    && nonnegative(value.budgetBytes) !== null
    && nonnegative(value.usageBytes) !== null
    && validTimestamp(value.lastManifestSuccessAt ?? null)
    && validTimestamp(value.lastMaintenanceSuccessAt ?? null)
    && (value.inventoryKnown === undefined || typeof value.inventoryKnown === 'boolean')
    && validRefreshHealth(value.refreshHealth) && validDownloadHealth(value.downloadHealth),
  );
}

export function validMediaReadinessStatus(value) {
  return validReadinessStatus(value, STATES);
}

// Fixed copy only: manager reasons/actions are never rendered as free text.
// Legacy DTOs and unknown state/cause pairs get no guessed instruction.
const FAILURE_DETAILS = Object.freeze({
  delayed: new Map([
    ['cloud_session_required', 'Sign in again to finish preparing offline images.'],
    ['cloud_access_required', 'The server did not authorize access to offline image information.'],
    ['device_authority_required', 'This computer does not have the required authorization for offline images.'],
    ['cloud_rebuild_required', 'Offline image information needs refreshing. The app handles this refresh; keep the desktop open.'],
    ['metadata_request_timed_out', 'The request for offline image information timed out.'],
    ['metadata_service_unavailable', 'The service providing offline image information could not be reached.'],
    ['cloud_service_unavailable', 'The server could not provide offline image information.'],
    ['local_service_unavailable', 'The local service could not process offline image information.'],
    ['media_metadata_sync_failed', 'The offline image information check did not finish.'],
    ['metadata_response_too_large', 'The offline image information response exceeded the supported size.'],
    ['local_control_unavailable', 'The connection to the local offline service is not ready.'],
    ['local_manifest_response_invalid', 'The local offline image information response could not be validated.'],
  ]),
  investigation_required: new Map([
    ['local_runtime_unavailable', 'Local offline services are not ready. Review Settings > Devices.'],
    ['protected_storage_unavailable', 'Protected offline storage is not ready. Review Settings > Devices.'],
    ['cloud_state_invalid', 'The server’s offline image status could not be validated. Review Settings > Devices.'],
    ['cloud_snapshot_invalid', 'The server’s offline image inventory could not be validated. Review Settings > Devices.'],
    ['cloud_changes_invalid', 'The server’s offline image updates could not be validated. Review Settings > Devices.'],
    ['local_manifest_response_invalid', 'Local offline image information could not be validated. Review Settings > Devices.'],
  ]),
});

// Causes that explain a condition without reporting a failure. These are not
// in FAILURE_DETAILS because they are not failures, and attaching them to a
// state would lose them: offline readiness is reported as current or catching
// up, exactly as it would be online (TCA-26100804 step 7).
// A Map, not an object: a cause arriving as `__proto__` or `toString` would
// otherwise resolve against Object.prototype. The existing suite tests for it.
const INFORMATIONAL_DETAILS = new Map([
  ['offline_no_connection',
    'This computer is offline, so it cannot check for newer photo information. Photos already stored here are unaffected.'],
]);

function failureDetail(status) {
  if (status.state === 'delayed' && status.cause === 'media_handoff_interrupted') {
    return 'The download handoff was interrupted. Its outcome is not yet established.';
  }
  if (status.schemaVersion === 2 && status.state === 'delayed' && status.cause === 'media_refresh_delayed') {
    return refreshDelayDetail(status);
  }
  if (status.schemaVersion === 2 && status.state === 'current' && status.cause === 'checking_for_updates') {
    return 'Checking for updates. Currently eligible media is available offline.';
  }
  if (status.schemaVersion === 2 && INFORMATIONAL_DETAILS.has(status.cause)) {
    return INFORMATIONAL_DETAILS.get(status.cause);
  }
  const details = FAILURE_DETAILS[status.state];
  if (!details) return null;
  return (status.schemaVersion === 2 ? details.get(status.cause) : null)
    || (status.state === 'delayed'
      ? 'The last attempt to prepare offline images did not finish.'
      : 'Offline image readiness could not be verified.');
}

export function formatMediaBytes(value) {
  if (nonnegative(value) === null) return null;
  if (value < 1024) return `${value} B`;
  if (value < 1024 ** 2) return `${(value / 1024).toFixed(1)} KiB`;
  if (value < 1024 ** 3) return `${(value / (1024 ** 2)).toFixed(1)} MiB`;
  return `${(value / (1024 ** 3)).toFixed(2)} GiB`;
}

const PRESENTATIONS = Object.freeze({
  not_enabled: Object.freeze({
    label: 'Not enabled',
    detail: 'Install offline order data before this computer can prepare protected media.',
    tone: 'neutral',
  }),
  preparing: Object.freeze({
    label: 'Preparing',
    detail: 'Protected-media information is being checked and prepared.',
    tone: 'amber',
  }),
  current: Object.freeze({
    label: 'Current',
    detail: 'All currently eligible protected media is available offline.',
    tone: 'green',
  }),
  catching_up: Object.freeze({
    label: 'Catching up',
    detail: 'Some currently eligible protected media is still being prepared.',
    tone: 'amber',
  }),
  capacity_limited: Object.freeze({
    label: 'Storage limited',
    detail: 'Protected-media storage cannot currently hold every eligible item. Review this device’s offline storage.',
    tone: 'amber',
  }),
  delayed: Object.freeze({
    label: 'Delayed',
    detail: 'The last attempt to prepare offline images did not finish.',
    tone: 'amber',
  }),
  investigation_required: Object.freeze({
    label: 'Investigation required',
    detail: 'A protected-media safety check needs administrator attention. Review this device’s offline services.',
    tone: 'red',
  }),
});

export function mediaReadinessPresentation(status, { loading = false, desktop = true } = {}) {
  if (!desktop) return Object.freeze({
    label: 'Desktop app required',
    detail: 'Protected-media readiness is available in the desktop app.',
    tone: 'neutral',
    valid: false,
  });
  if (loading) return Object.freeze({
    label: 'Checking', detail: 'Reading this desktop’s protected-media status.', tone: 'neutral', valid: false,
  });
  if (!validMediaReadinessStatus(status)) return Object.freeze({
    label: 'Status unavailable',
    detail: 'Protected-media readiness could not be verified. Review this device’s offline services.',
    tone: 'red',
    valid: false,
  });
  const base = PRESENTATIONS[status.state];
  const transfer = status.transfer
    && PURPOSE_LABELS[status.transfer.purpose]
    && nonnegative(status.transfer.transferredBytes) !== null
    && nonnegative(status.transfer.expectedBytes) !== null
    && status.transfer.transferredBytes <= status.transfer.expectedBytes
    ? Object.freeze({
      label: PURPOSE_LABELS[status.transfer.purpose],
      transferredBytes: status.transfer.transferredBytes,
      expectedBytes: status.transfer.expectedBytes,
    })
    : null;
  return Object.freeze({ ...base, detail: failureDetail(status) || base.detail, valid: true, transfer });
}

/**
 * Whether this status's counts were actually read. The projection reports an
 * inventory it has not read as `preparing` with zero eligible, and a count line
 * drawn from it would state a zero nobody established. Both tabs asked this
 * question in their own words (TCA-26100804 closing audit).
 */
export function inventoryUnread(status) {
  return status?.inventoryKnown === false
    || (status?.state === 'preparing' && status?.counts?.eligible === 0);
}

export function catalogMediaReadinessPresentation(status) {
  if (!validReadinessStatus(status, CATALOG_STATES)) return Object.freeze({
    label: 'Reference image status unavailable',
    detail: 'Offline reference image readiness could not be verified.',
    valid: false,
  });
  const views = {
    current: ['Reference images current', 'All eligible product reference images are available offline.'],
    preparing: ['Reference images preparing', 'Product reference image information is being checked.'],
    catching_up: ['Reference images downloading', 'Eligible product reference images are still being prepared.'],
    partially_kept: ['Reference images partially kept', 'Some product reference images are not currently kept on this computer.'],
    delayed: ['Reference images delayed', 'The last attempt to prepare offline images did not finish.'],
    investigation_required: ['Reference images need investigation', 'Offline image readiness could not be verified.'],
    not_available: ['No offline reference images', 'No eligible product reference images are available for offline use.'],
  };
  const [label, detail] = views[status.state];
  return Object.freeze({ label, detail: failureDetail(status) || detail, valid: true });
}

export function mediaReadinessNotice(status) {
  if (!validMediaReadinessStatus(status) || status.state === 'not_enabled') return null;
  const zeroAvailable = status.counts.eligible > 0 && status.counts.available === 0;
  if (['preparing', 'catching_up'].includes(status.state)) {
    return zeroAvailable ? Object.freeze({
      tone: 'amber',
      text: 'Offline media is still being prepared. Structured order data remains available.',
    }) : null;
  }
  if (status.state === 'capacity_limited') return Object.freeze({
    tone: 'amber', text: 'Offline media storage needs attention. Review Settings > Devices.',
  });
  if (status.state === 'delayed') return Object.freeze({
    tone: 'amber', text: status.cause === 'media_refresh_delayed'
      ? mediaReadinessPresentation(status).detail
      : `Offline media preparation is delayed. ${mediaReadinessPresentation(status).detail}`,
  });
  if (status.state === 'investigation_required') return Object.freeze({
    tone: 'red', text: mediaReadinessPresentation(status).detail,
  });
  return null;
}

export const MEDIA_PURPOSE_LABELS = PURPOSE_LABELS;
